import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import Fetch from "../../services/Fetch";
import { Box, Checkbox, FormControlLabel, TextField, Typography } from "@mui/material";
import { AsyncPaginate } from "react-select-async-paginate";
import MultipleSelectChip from "../../components/MultiSelect";
import SnackbarAlert from "../../components/SnackBar";
import Image1 from '../../images/courses/image1.png';
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { buildCourseFormData } from "../../helper/CourseFormData";
import { parsePaginatedResponse } from "../../utils/parsePaginatedResponse";
import FormSideImageLayout, { previewImageFile } from "../../components/FormSideImageLayout";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useConstants } from "../../hooks/UseConstants";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";

function UpdateCourse() {
     const { host, language } = useConstants();
     const { wait } = useContext(AuthContext);
     const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
     const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
     const [option, setOption] = useState('');
     const [course, setCourse] = useState(null);
     const [image, setImage] = useState('');
     const [categoryId, setCategoryId] = useState('');
     const [majors, setMajors] = useState([]);
     const [selectedMajors, setSelectedMajors] = useState([]);
     const [nameEn, setNameEn] = useState('');
     const [nameAr, setNameAr] = useState('');
     const [descriptionEn, setDescriptionEn] = useState('');
     const [descriptionAr, setDescriptionAr] = useState('');
     const [price, setPrice] = useState('');
     const [discountPrice, setDiscountPrice] = useState('');
     const [isFree, setIsFree] = useState(false);
     const [levelAr, setLevelAr] = useState('');
     const [levelEn, setLevelEn] = useState('');
     const [objectivesAr, setObjectivesAr] = useState('');
     const [objectivesEn, setObjectivesEn] = useState('');
     const [prerequisitesAr, setPrerequisitesAr] = useState('');
     const [prerequisitesEn, setPrerequisitesEn] = useState('');
     const [discountStartAt, setDiscountStartAt] = useState('');
     const [discountEndAt, setDiscountEndAt] = useState('');
     const [introVideo, setIntroVideo] = useState('');
     const param = useParams();
     const navigate = useNavigate();

     const loadOptions = async (search, loadedOptions, { page }) => {
          const result = await Fetch(
               `${host}/categories?search=${encodeURIComponent(search || '')}&page=${page}&per_page=20`,
               'GET',
               null
          );
          const { items, pagination } = parsePaginatedResponse(result);
          return {
               options: items.map(
                    item => ({ value: item.id, label: language === 'ar' ? item.name_ar : item.name_en })
               ),
               hasMore: pagination.current_page < pagination.last_page,
               additional: { page: page + 1 },
          };
     };

     const getMajors = async () => {
          const result = await Fetch(`${host}/majors?per_page=100`, "GET", null);
          const { items } = parsePaginatedResponse(result);
          setMajors(items);
     };

     const getCourse = async () => {
          const result = await Fetch(`${host}/courses/${param.id}/show`, 'GET', null);

          if (result.status === 200) {
               const data = result.data.data;
               setCourse(data);
               setCategoryId(data.category?.id || '');
               setSelectedMajors((data.majors || []).map((major) => major.id));
               setOption({
                    value: data.category?.id,
                    label: language === 'ar' ? data.category?.name_ar : data.category?.name_en,
               });
               setNameEn(data.name_en || '');
               setNameAr(data.name_ar || '');
               setDescriptionEn(data.description_en || '');
               setDescriptionAr(data.description_ar || '');
               setPrice(data.price || '');
               setDiscountPrice(data.discount_price || '');
               setIsFree(!!data.is_free);
               setLevelAr(data.level_ar || '');
               setLevelEn(data.level_en || '');
               setObjectivesAr(data.objectives_ar || '');
               setObjectivesEn(data.objectives_en || '');
               setPrerequisitesAr(data.prerequisites_ar || '');
               setPrerequisitesEn(data.prerequisites_en || '');
               setDiscountStartAt(data.discount_start_at?.slice(0, 10) || '');
               setDiscountEndAt(data.discount_end_at?.slice(0, 10) || '');
          }
     };

     const updateCourse = async () => {
          setSendWait(true);

          const formData = buildCourseFormData({
               nameEn,
               nameAr,
               descriptionEn,
               descriptionAr,
               price,
               discountPrice,
               isFree,
               levelAr,
               levelEn,
               objectivesAr,
               objectivesEn,
               prerequisitesAr,
               prerequisitesEn,
               discountStartAt,
               discountEndAt,
               image,
               introVideo,
               categoryId,
               majors: selectedMajors,
          });
          
          const result = await Fetch(`${host}/teacher/courses/${param.id}/update`, 'POST', formData);

          if (result.status === 200) {
               navigate(`/course-details/${param.id}`);
          } else if (result.status === 422) {
               setSnackBar('error', result.data.errors[0]);
          }

          setSendWait(false);
     };

     useEffect(() => {
          const load = async () => {
               try {
                    await Promise.all([getMajors(), getCourse()]);
               } finally {
                    setGetWait(false);
               }
          };

          load();
     }, [param.id]);

     const fieldSx = { width: '100%' };

     return (
          <>
               <PageContent wait={wait} loading={getWait}>
                    <PageHero
                         title={language === 'ar' ? 'تعديل الدورة' : 'Update course'}
                         subtitle={language === 'ar' ? 'حدّث معلومات الدورة وخصوماتها ووسائطها' : 'Update course details, discounts, and media'}
                         icon={<EditOutlinedIcon />}
                    />
                    <Box sx={{ bgcolor: 'background.paper', boxShadow: 3, borderRadius: 3, overflow: 'hidden' }}>
                         <FormSideImageLayout
                              title={language === 'ar' ? 'تحديث الدورة' : 'Update Course'}
                              illustrationSrc={Image1}
                              previewId="course-update-image"
                              previewSrc={resolveCourseImage(course)}
                              uploadLabel={language === 'ar' ? 'صورة الدورة' : 'Course cover'}
                              onImageChange={(e) => {
                                   previewImageFile(e, 'course-update-image');
                                   setImage(e.target.files[0]);
                              }}
                              onSubmit={updateCourse}
                              submitLabel={language === 'ar' ? 'حفظ التعديلات' : 'Save changes'}
                              loading={sendWait}
                         >
                              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField sx={fieldSx} value={nameEn} onChange={(e) => setNameEn(e.target.value)} label={language === 'ar' ? 'الاسم بالإنجليزية' : 'English Name'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} value={nameAr} onChange={(e) => setNameAr(e.target.value)} label={language === 'ar' ? 'الاسم بالعربية' : 'Arabic Name'} variant="outlined" size="small" />
                              </Box>
                              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField sx={fieldSx} value={descriptionEn} onChange={(e) => setDescriptionEn(e.target.value)} label={language === 'ar' ? 'الوصف بالإنجليزية' : 'English Description'} multiline rows={3} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} value={descriptionAr} onChange={(e) => setDescriptionAr(e.target.value)} label={language === 'ar' ? 'الوصف بالعربية' : 'Arabic Description'} multiline rows={3} variant="outlined" size="small" />
                              </Box>
                              <Box sx={{ mt: 2 }}>
                                   <AsyncPaginate
                                        className="text-black"
                                        value={option}
                                        loadOptions={loadOptions}
                                        onChange={(opt) => {
                                             setOption(opt);
                                             setCategoryId(opt?.value);
                                        }}
                                        placeholder={language === 'ar' ? 'اختر التصنيف' : 'Select Category'}
                                        additional={{ page: 1 }}
                                   />
                              </Box>
                              <Box sx={{ mt: 2 }}>
                                   <MultipleSelectChip selected={selectedMajors} title={language === 'ar' ? 'التخصصات' : 'Majors'} data={majors} onChange={setSelectedMajors} />
                              </Box>
                              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField sx={fieldSx} type="number" value={price} onChange={(e) => setPrice(e.target.value)} label={language === 'ar' ? 'السعر' : 'Price'} variant="outlined" size="small" disabled={isFree} />
                                   <TextField sx={fieldSx} type="number" value={discountPrice} onChange={(e) => setDiscountPrice(e.target.value)} label={language === 'ar' ? 'سعر الخصم' : 'Discount Price'} variant="outlined" size="small" disabled={isFree} />
                              </Box>
                              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField
                                        sx={fieldSx}
                                        type="date"
                                        value={discountStartAt}
                                        onChange={(e) => setDiscountStartAt(e.target.value)}
                                        label={language === 'ar' ? 'بداية الخصم' : 'Discount starts'}
                                        variant="outlined"
                                        size="small"
                                        disabled={isFree}
                                        InputLabelProps={{ shrink: true }}
                                   />
                                   <TextField
                                        sx={fieldSx}
                                        type="date"
                                        value={discountEndAt}
                                        onChange={(e) => setDiscountEndAt(e.target.value)}
                                        label={language === 'ar' ? 'نهاية الخصم' : 'Discount ends'}
                                        variant="outlined"
                                        size="small"
                                        disabled={isFree}
                                        InputLabelProps={{ shrink: true }}
                                   />
                              </Box>
                              <FormControlLabel
                                   sx={{ mt: 1 }}
                                   control={<Checkbox checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />}
                                   label={language === 'ar' ? 'دورة مجانية' : 'Free course'}
                              />
                              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                                   <TextField sx={fieldSx} value={levelEn} onChange={(e) => setLevelEn(e.target.value)} label={language === 'ar' ? 'المستوى (إنجليزي)' : 'Level (EN)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} value={levelAr} onChange={(e) => setLevelAr(e.target.value)} label={language === 'ar' ? 'المستوى (عربي)' : 'Level (AR)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} multiline rows={2} value={objectivesEn} onChange={(e) => setObjectivesEn(e.target.value)} label={language === 'ar' ? 'الأهداف بالإنجليزية' : 'Objectives (EN)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} multiline rows={2} value={objectivesAr} onChange={(e) => setObjectivesAr(e.target.value)} label={language === 'ar' ? 'الأهداف بالعربية' : 'Objectives (AR)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} multiline rows={2} value={prerequisitesEn} onChange={(e) => setPrerequisitesEn(e.target.value)} label={language === 'ar' ? 'المتطلبات بالإنجليزية' : 'Prerequisites (EN)'} variant="outlined" size="small" />
                                   <TextField sx={fieldSx} multiline rows={2} value={prerequisitesAr} onChange={(e) => setPrerequisitesAr(e.target.value)} label={language === 'ar' ? 'المتطلبات بالعربية' : 'Prerequisites (AR)'} variant="outlined" size="small" />
                              </Box>
                              <Box sx={{ mt: 2, p: 2, borderRadius: 2, bgcolor: 'background.default' }}>
                                   <Typography variant="body2" color="text.secondary" className="!mb-1">
                                        {language === 'ar' ? 'استبدال الفيديو التعريفي' : 'Replace intro video'}
                                   </Typography>
                                   <input type="file" accept="video/*" onChange={(e) => setIntroVideo(e.target.files[0])} />
                              </Box>
                         </FormSideImageLayout>
                    </Box>
               </PageContent>
               <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
          </>
     );
}

export default UpdateCourse;